import React from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";

// TZ-789 admin: renders the response from POST /onboarding/{staff,students}.
// Expected shape (PR1/PR2 of TZ-789 backend):
//   { summary: { created, skipped, errors },
//     created: [{ row_number, staff_member_id | student_id }],
//     skipped: [{ row_number, reason }],
//     errors:  [{ row_number, errors: [...] }] }
// `kind` is "staff" or "student" — used to label the created-id column.

const ResultPanel = ({ result, kind }) => {
  if (!result) return null;

  const summary = result.summary || { created: 0, skipped: 0, errors: 0 };
  const idKey = kind === "staff" ? "staff_member_id" : "student_id";
  const idLabel = kind === "staff" ? "Staff ID" : "Student ID";

  return (
    <Paper style={{ padding: 16, marginTop: 16 }}>
      <Typography variant="subtitle1" gutterBottom>
        Upload result
      </Typography>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <Chip
          label={`Created: ${summary.created}`}
          color="primary"
          size="small"
        />
        <Chip
          label={`Skipped (already exists): ${summary.skipped}`}
          size="small"
        />
        <Chip
          label={`Errors: ${summary.errors}`}
          color={summary.errors > 0 ? "secondary" : "default"}
          size="small"
        />
      </div>

      {result.errors && result.errors.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Errors — fix these rows and re-upload</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0, display: "block" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 80 }}>Row</TableCell>
                  <TableCell>Problems</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.errors.map((row) => (
                  <TableRow key={`err_${row.row_number}`}>
                    <TableCell>{row.row_number}</TableCell>
                    <TableCell>
                      {(row.errors || []).map((msg, idx) => (
                        <div key={`${row.row_number}_${idx}`}>{msg}</div>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {result.skipped && result.skipped.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Skipped (already on this school&apos;s roster)</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0, display: "block" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 80 }}>Row</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.skipped.map((row) => (
                  <TableRow key={`skip_${row.row_number}`}>
                    <TableCell>{row.row_number}</TableCell>
                    <TableCell>{row.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {result.created && result.created.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Created ({result.created.length})</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0, display: "block" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 80 }}>Row</TableCell>
                  <TableCell>{idLabel}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.created.map((row) => (
                  <TableRow key={`ok_${row.row_number}`}>
                    <TableCell>{row.row_number}</TableCell>
                    <TableCell>{row[idKey]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};

ResultPanel.propTypes = {
  result: PropTypes.shape({
    summary: PropTypes.object,
    created: PropTypes.array,
    skipped: PropTypes.array,
    errors:  PropTypes.array,
  }),
  kind: PropTypes.oneOf(["staff", "student"]).isRequired,
};

ResultPanel.defaultProps = {
  result: null,
};

export default ResultPanel;
